import React, { useState } from 'react';
import { colors, radius, fontStack } from '../design-system/tokens';

/**
 * TransactionRow — single line in a transactions list.
 *
 * Props:
 *   - tx: { id, log_type, amount, currency, category, description, date, notes }
 *   - onDelete(tx)?
 */
export default function TransactionRow({ tx, onDelete }) {
  const [hover, setHover] = useState(false);
  const isIncome = tx.log_type === 'income';

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 12px',
        borderRadius: radius.sm,
        backgroundColor: hover ? 'var(--bg-hover)' : 'transparent',
        transition: 'background-color 120ms ease',
        fontFamily: fontStack,
      }}
    >
      {/* Icon */}
      <span
        aria-hidden="true"
        style={{
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: '50%',
          backgroundColor: isIncome ? 'rgba(111, 207, 151, 0.14)' : 'var(--bg-hover)',
          color: isIncome ? 'var(--success)' : colors.textSecondary,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isIncome ? <ArrowUp /> : <ArrowDown />}
      </span>

      {/* Description */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: colors.textPrimary,
            lineHeight: 1.3,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {tx.description || tx.category || (isIncome ? 'Income' : 'Expense')}
        </div>
        <div
          style={{
            fontSize: 11,
            color: colors.textSecondary,
            marginTop: 2,
            display: 'flex',
            gap: 8,
            alignItems: 'center',
          }}
        >
          <span>{tx.category || 'Other'}</span>
          <span aria-hidden="true">·</span>
          <span>{formatDate(tx.date)}</span>
        </div>
      </div>

      {/* Amount */}
      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: isIncome ? 'var(--success)' : colors.textPrimary,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        {isIncome ? '+' : '−'}£{Number(tx.amount || 0).toFixed(2)}
      </div>

      {/* Delete on hover */}
      {onDelete && (
        <button
          type="button"
          onClick={() => onDelete(tx)}
          aria-label="Delete transaction"
          style={{
            width: 28,
            height: 28,
            padding: 0,
            border: 'none',
            background: 'transparent',
            borderRadius: 999,
            cursor: 'pointer',
            color: colors.textSecondary,
            opacity: hover ? 1 : 0,
            transition: 'opacity 120ms ease',
            fontFamily: fontStack,
            fontSize: 16,
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}

function formatDate(d) {
  if (!d) return '';
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', {
      month: 'short',
      day: 'numeric',
    });
  } catch (_) {
    return d;
  }
}

function ArrowUp() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 19V5" />
      <path d="M5 12l7-7 7 7" />
    </svg>
  );
}
function ArrowDown() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14" />
      <path d="M5 12l7 7 7-7" />
    </svg>
  );
}
